<template>
  <span v-if="hasPermission">
    <!-- Trigger button -->
    <v-btn
      small
      outlined
      color="primary"
      class="ml-2"
      :loading="loading"
      :disabled="loading"
      @click="openDialog"
    >
      <v-icon small left>mdi-robot</v-icon>
      {{ $t('ailetter.draftbtn') || 'AI Draft' }}
    </v-btn>

    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="pb-2">
          <v-icon color="primary" class="mr-2">mdi-robot</v-icon>
          {{ $t('ailetter.dialogtitle') || 'AI Letter Template Draft' }}
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">

          <!-- Letter type -->
          <v-text-field
            outlined
            dense
            :label="$t('ailetter.lettertype') || 'Letter Type'"
            v-model="letterType"
            :placeholder="'e.g. Increment Letter, Promotion Letter, Confirmation Letter, Warning Letter'"
            class="mb-2"
          />

          <!-- Additional instructions -->
          <v-textarea
            outlined
            dense
            rows="3"
            :label="$t('ailetter.instructions') || 'Additional Instructions (optional)'"
            v-model="additionalInstructions"
            :placeholder="'e.g. Include a paragraph about performance expectations, formal tone, Malaysian company'"
            hide-details
          />

          <!-- Available placeholders hint -->
          <div v-if="availablePlaceholders.length" class="mt-3">
            <p class="caption grey--text mb-1">Available placeholders — AI will use these in the template:</p>
            <div style="max-height:80px; overflow-y:auto;">
              <v-chip
                v-for="p in availablePlaceholders"
                :key="p"
                x-small
                outlined
                class="ma-1"
              >{{ wrapPlaceholder(p) }}</v-chip>
            </div>
          </div>

          <!-- Draft preview -->
          <div v-if="draftResult" class="mt-4">
            <v-divider class="mb-3" />
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="caption grey--text text--darken-1 font-weight-medium">
                {{ $t('ailetter.preview') || 'Draft preview' }}
              </span>
              <v-btn x-small text color="primary" @click="copyToClipboard">
                <v-icon x-small left>mdi-content-copy</v-icon>
                {{ $t('ailetter.copy') || 'Copy' }}
              </v-btn>
            </div>
            <v-card outlined class="pa-3" style="max-height:240px; overflow-y:auto;">
              <pre style="white-space:pre-wrap; font-size:0.8rem; font-family:inherit; line-height:1.6;">{{ draftResult }}</pre>
            </v-card>
          </div>

          <!-- Error -->
          <v-alert v-if="errMsg" type="error" dense text class="mt-3 mb-0">
            {{ errMsg }}
          </v-alert>

        </v-card-text>

        <v-card-actions class="pt-0 pb-3 px-4">
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!letterType.trim() || loading"
            @click="generateDraft"
          >
            <v-icon small left>mdi-auto-fix</v-icon>
            {{ draftResult ? ($t('ailetter.regenerate') || 'Regenerate') : ($t('ailetter.generate') || 'Generate') }}
          </v-btn>

          <v-btn
            v-if="draftResult"
            color="success"
            :disabled="loading"
            @click="insertIntoEditor"
          >
            <v-icon small left>mdi-check</v-icon>
            {{ $t('ailetter.insert') || 'Insert into Editor' }}
          </v-btn>

          <v-spacer />

          <v-btn text @click="closeDialog">
            {{ $t('button.cancel') || 'Cancel' }}
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="3000">
      {{ snackbarMsg }}
    </v-snackbar>

  </span>
</template>

<script>
export default {
  name: "AILetterDraftBtn",

  props: {
    // Letter type pre-filled from template type (e.g. "Movement", "Confirmation")
    templateTypeName: {
      type: String,
      default: ""
    },
    // Template name for more context (e.g. "Increment Letter")
    templateName: {
      type: String,
      default: ""
    },
    // Available placeholder columns from letterColumn
    letterColumns: {
      type: Array,
      default: () => []
    },
    // Quill editor ref for inserting text
    quillRef: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      hasPermission: false,
      dialog: false,
      loading: false,
      letterType: "",
      additionalInstructions: "",
      draftResult: "",
      errMsg: "",
      snackbar: false,
      snackbarMsg: "",
      snackbarColor: "success",
    };
  },

  mounted() {
    this.checkPermission();
  },

  computed: {
    availablePlaceholders() {
      return this.letterColumns
        .filter(c => c.columnname && c.columnname !== 'CURDATE()')
        .map(c => c.columnname)
        .slice(0, 20);
    }
  },

  watch: {
    templateName(val) {
      if (val && !this.letterType) this.letterType = val;
    }
  },

  methods: {
    checkPermission() {
      try {
        const token = this.$session.get("access_token");
        if (!token) return;
        const sessions = JSON.parse(token);
        if (sessions && sessions.funcaccess && sessions.funcaccess.length > 0) {
          const result = sessions.funcaccess.filter(e => e.funcname === "HR Chat");
          this.hasPermission = result.length > 0 && result[0].canaccess === "Y";
        }
      } catch (e) {
        this.hasPermission = false;
      }
    },

    wrapPlaceholder(p) {
      return '{{' + p + '}}';
    },

    openDialog() {
      this.dialog = true;
      this.draftResult = "";
      this.errMsg = "";
      // Pre-fill letter type from template name or type
      this.letterType = this.templateName || this.templateTypeName || "";
    },

    closeDialog() {
      this.dialog = false;
      this.draftResult = "";
      this.errMsg = "";
    },

    async generateDraft() {
      if (!this.letterType.trim()) return;

      this.loading = true;
      this.errMsg = "";
      this.draftResult = "";

      // Build placeholder list for AI context
      const placeholders = this.letterColumns
        .map(c => `{{${c.columnname}}} = ${c.display}`)
        .join(', ');

      try {
        const res = await this.$store.dispatch("post", {
          url: "/ai/chat",
          payload: {
            messages: [{
              role: "user",
              content: `Draft a professional ${this.letterType} template for a Malaysian company.
${this.additionalInstructions ? 'Additional instructions: ' + this.additionalInstructions : ''}

Use these placeholders where appropriate (format: {{columnname}}):
${placeholders || 'psnname (staff name), CURDATE() (today date), basicsal (basic salary), jobtitle (job title), deptname (department), grddesc (grade), compnyname (company name)'}

The letter must include:
- Company letterhead area at top
- Date using {{CURDATE()}}
- Staff name and salutation using {{psnname}}
- Professional body paragraphs with relevant placeholders
- Proper closing and signature block

Return only the letter content, ready to paste into a letter editor.`
            }],
          }
        });

        if (res.data.status === 200) {
          this.draftResult = res.data.reply;
        } else {
          this.errMsg = res.data.error || "AI service error. Please try again.";
        }
      } catch (err) {
        this.errMsg = "Cannot connect to AI service. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    insertIntoEditor() {
      if (!this.draftResult) return;

      if (this.quillRef && this.quillRef.quill) {
        const quill = this.quillRef.quill;
        const range = quill.selection.savedRange;
        const idx = range ? range.index : 0;
        quill.insertText(idx, this.draftResult);
      } else {
        this.$emit("draft-ready", this.draftResult);
      }

      this.showSnackbar(this.$t('ailetter.inserted') || "Draft inserted into editor", "success");
      this.closeDialog();
    },

    copyToClipboard() {
      navigator.clipboard.writeText(this.draftResult)
        .then(() => this.showSnackbar(this.$t('ailetter.copied') || "Copied to clipboard", "success"))
        .catch(() => this.showSnackbar("Copy failed", "error"));
    },

    showSnackbar(msg, color = "success") {
      this.snackbarMsg = msg;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>